import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Card,
  CardContent
} from '@mui/material';
import ScrollableTabs from '@components/mobile/ScrollableTabs';
import ResponsiveDataView from '@components/mobile/ResponsiveDataView';
import {
  ArrowBack,
  CheckCircle,
  Schedule,
  Groups as MeetingIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import principalAPI from '../../services/principalAPI';
import { useAuth } from '../../context/AuthContext';
import EmptyState from '../../components/EmptyState';

const ParentMeetings = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [meetings, setMeetings] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('all');
  const [error, setError] = useState(null);

  const fetchClasses = useCallback(async () => {
    try {
      const response = await principalAPI.getClasses();
      setClasses(response.data.classes || []);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  }, []);

  const fetchMeetings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching meetings...');
      const response = await principalAPI.getMeetings();
      console.log('Meetings response:', response);
      console.log('Meetings data:', response.data);
      const allMeetings = response.data.meetings || [];
      console.log('All meetings:', allMeetings);
      
      // Filter meetings based on active tab
      let filteredMeetings = allMeetings;
      if (activeTab === 0) {
        // Upcoming: Scheduled and Rescheduled
        filteredMeetings = allMeetings.filter(m => m.status === 'Scheduled' || m.status === 'Rescheduled');
      } else {
        // Completed: only Completed status
        filteredMeetings = allMeetings.filter(m => m.status === 'Completed');
      }
      
      // Apply class filter if selected
      if (selectedClass !== 'all') {
        filteredMeetings = filteredMeetings.filter(m => m.student?.class_id === selectedClass);
      }
      
      console.log('Filtered meetings:', filteredMeetings);
      setMeetings(filteredMeetings);
    } catch (error) {
      console.error('Error fetching meetings:', error);
      console.error('Error details:', error.response?.data || error.message);
      setError('Failed to load parent meetings. Please try again.');
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, selectedClass]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formatDateTime = (dateTime) => {
    return new Date(dateTime).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Scheduled':
        return 'primary';
      case 'Rescheduled':
        return 'warning';
      case 'Completed':
        return 'success';
      case 'Cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'Scheduled':
        return 'Scheduled';
      case 'Rescheduled':
        return 'Rescheduled';
      case 'Completed':
        return 'Completed';
      case 'Cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Parent Teacher Meetings
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/principal/dashboard')}
        >
          Back to Dashboard
        </Button>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} alignItems={{ xs: 'stretch', sm: 'center' }} mb={3}>
          <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
            <InputLabel>Filter by Class</InputLabel>
            <Select
              value={selectedClass}
              label="Filter by Class"
              onChange={(e) => setSelectedClass(e.target.value)}
            >
              <MenuItem value="all">All Classes</MenuItem>
              {classes.map((cls) => (
                <MenuItem key={cls.id} value={cls.id}>
                  {cls.grade && cls.section ? `${cls.grade} - ${cls.section}` : cls.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ mb: 3, p: 0.75 }}>
          <ScrollableTabs
            value={activeTab}
            onChange={handleTabChange}
            TabIndicatorProps={{ sx: { display: 'none' } }}
            sx={{
              minHeight: 0,
              '& .MuiTabs-flexContainer': { gap: 1 },
              '& .MuiTab-root': {
                minHeight: 0,
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                py: 1,
                color: 'primary.main',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                '&:hover': {
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2)
                },
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  }
                }
              }
            }}
          >
            <Tab
              icon={<Schedule />}
              label="Upcoming Schedule"
              iconPosition="start"
            />
            <Tab
              icon={<CheckCircle />}
              label="Completed"
              iconPosition="start"
            />
          </ScrollableTabs>
        </Paper>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading meetings...</Typography>
          </Box>
        ) : meetings.length === 0 ? (
          <EmptyState
            icon={MeetingIcon}
            title={`No ${activeTab === 0 ? 'upcoming' : 'completed'} meetings found`}
            description={selectedClass !== 'all' ? 'Try a different class filter.' : undefined}
            variant={selectedClass !== 'all' ? 'no-results' : 'empty'}
            dense
          />
        ) : (
          <ResponsiveDataView
            rows={meetings}
            keyExtractor={(meeting) => meeting.id}
            renderCard={(meeting) => (
              <Card variant="outlined" sx={{ mb: 1.5 }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                    <Typography variant="body2" fontWeight="medium">{meeting.student?.name}</Typography>
                    <Chip label={getStatusLabel(meeting.status)} color={getStatusColor(meeting.status)} size="small" />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {meeting.student?.class ?
                      (meeting.student.class.grade && meeting.student.class.section ?
                        `${meeting.student.class.grade} - ${meeting.student.class.section}` :
                        meeting.student.class.name) :
                      'Unknown Class'} · {formatDateTime(meeting.scheduled_datetime)}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Teacher: {meeting.teacher?.user?.name || '—'}
                  </Typography>
                  <Typography variant="body2">
                    Parent: {meeting.parent?.name || '—'} {meeting.parent?.phone ? `(${meeting.parent.phone})` : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Agenda: {meeting.agenda || 'No agenda specified'}
                  </Typography>
                  {activeTab === 1 && (
                    <Typography variant="body2" color="text.secondary">
                      Notes: {meeting.notes || 'No notes'}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            )}
            table={
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Student</strong></TableCell>
                      <TableCell><strong>Class</strong></TableCell>
                      <TableCell><strong>Teacher</strong></TableCell>
                      <TableCell><strong>Parent</strong></TableCell>
                      <TableCell><strong>Meeting Date & Time</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Agenda</strong></TableCell>
                      {activeTab === 1 && <TableCell><strong>Notes</strong></TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {meetings.map((meeting) => (
                      <TableRow key={meeting.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {meeting.student?.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {meeting.student?.class ?
                            (meeting.student.class.grade && meeting.student.class.section ?
                              `${meeting.student.class.grade} - ${meeting.student.class.section}` :
                              meeting.student.class.name) :
                            'Unknown Class'}
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {meeting.teacher?.user?.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {meeting.teacher?.subjects?.[0] || 'No subject'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {meeting.parent?.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {meeting.parent?.phone}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {formatDateTime(meeting.scheduled_datetime)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(meeting.status)}
                            color={getStatusColor(meeting.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 200 }}>
                            {meeting.agenda || 'No agenda specified'}
                          </Typography>
                        </TableCell>
                        {activeTab === 1 && (
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 200 }}>
                              {meeting.notes || 'No notes'}
                            </Typography>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            }
          />
        )}
      </Paper>
    </Container>
  );
};

export default ParentMeetings;